import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import { computed, makeObservable } from 'mobx';
import { observer, Provider } from 'mobx-react';
import * as React from 'react';
import { CredentialStore } from '../../../api/CredentialStore';
import { ImpersonationContextKey, ModalChildComponentContextKey } from '../../../models';
import {
	AppState,
	FullScreenModalViewModelKey,
	TextMessagingViewModelKey,
	UserSessionViewModelKey,
} from '../../../models/AppState';
import { AdminUserSessionContext } from '../../../viewmodels/AdminViewModels';
import { FullScreenModalViewModel, IUserSessionContextConfig } from '../../../viewmodels/AppViewModels';
import { SonnerToast } from '../../../web/components/SonnerToast';
import { TooltipProvider } from '../../../web/components/Tooltip';
import { AdminDrawerProvider } from '../../../web/components-v2/Drawer/AppDrawer';
import { AdminAgentStreamingMutationProvider } from '../../../web/containers/templates/AssistantChat';
import { NuqsRR5Adapter } from '../../../web/NuqsAdapter';
import { LevReactQueryDevtools } from '../../../web/ReactQueryDevtools';
import { ReactQueryProvider } from '../../../web/ReactQueryProvider';
import { AdminRouter } from '../AdminRouter';

interface IProps extends IEventLoggingComponentProps {
	userSessionConfig?: IUserSessionContextConfig;
}

interface IState {
	isLoading?: boolean;
}

class _AdminApp extends React.Component<IProps, IState> {
	private userSession: AdminUserSessionContext;

	private mMounted: boolean;

	constructor(props: IProps) {
		super(props);

		makeObservable<_AdminApp, 'contextProviderProps'>(this, {
			contextProviderProps: computed,
		});

		AppState.environment.appType = 'admin';

		const userSessionConfig: IUserSessionContextConfig = {
			...this.props.userSessionConfig,
			apiConfig: {
				...(
					this.props.userSessionConfig || {
						apiConfig: {
							baseUrl: import.meta.env.VITE_API_URL,
						},
					}
				).apiConfig,
				credentialStore: new CredentialStore(),
			},
		};
		this.userSession = new AdminUserSessionContext(userSessionConfig);

		AppState[UserSessionViewModelKey] = this.userSession;
		AppState[FullScreenModalViewModelKey] = new FullScreenModalViewModel();
		AppState[TextMessagingViewModelKey] = null; // required to stop admin app from crashing due to shared components injecting text messaging
		this.state = {
			isLoading: true,
		};
	}

	public componentDidMount() {
		this.mMounted = true;
		const promise = this.userSession.load();
		const onFinish = () => {
			if (this.mMounted) {
				this.setState({
					isLoading: false,
				});
			}
		};
		if (promise) {
			promise.then(onFinish).catch(onFinish);
		} else {
			onFinish();
		}
	}

	public render() {
		const { isLoading } = this.state;

		if (isLoading) {
			return null;
		}

		return (
			<TooltipProvider>
				<ReactQueryProvider>
					<Provider {...this.contextProviderProps}>
						<NuqsRR5Adapter>
							<AdminAgentStreamingMutationProvider>
								<AdminDrawerProvider>
									<div className='root admin-app'>
										<AdminRouter />
									</div>
								</AdminDrawerProvider>
							</AdminAgentStreamingMutationProvider>
						</NuqsRR5Adapter>
					</Provider>
					<LevReactQueryDevtools />
				</ReactQueryProvider>
				<SonnerToast />
			</TooltipProvider>
		);
	}

	private get contextProviderProps(): any {
		return {
			...AppState,
			[ImpersonationContextKey]: null,
			[ModalChildComponentContextKey]: null,
		};
	}
}

const AdminAppAsObserver = observer(_AdminApp);
export const AdminApp = withEventLogging(AdminAppAsObserver, 'AdminApp');
